import React, { useCallback, useEffect, useState } from 'react';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { PieChart, Pie, Cell, Legend } from 'recharts';
import { getMarketingDashBoardData } from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import noDataIcon from '../../shared/assets/png/noDataFound.png';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import AiPulsePanel from '../components/ai/AiPulsePanel';
import DashboardFilters from '../../shared/components/ui/DashboardFilters';
import { useSmartBack, shouldShowModuleHomeBack } from '../../shared/navigation';
import { useLocation } from 'react-router-dom';

const MarketingDashboard = () => {
  const userId = Cookies.get('userId');
  const location = useLocation();
  const { isProjectsLoading } = useProjectsList();
  const [isloading, setisLoading] = useState(false);
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/dashboard' });
  const showHomeBack = shouldShowModuleHomeBack('marketing', location);
  const [year, setYear] = useState(2025);

  const decrementYear = () => setYear((prev) => prev - 1);
  const incrementYear = () => setYear((prev) => prev + 1);
  // DashboardFilters owns the project picker (and auto-selects the first
  // project), and hands the selection back here.
  const [projectID, setProjectID] = useState('');
  const handleFiltersChange = useCallback(({ projectID: selected }) => {
    setProjectID(selected);
  }, []);

  const parameters = year && projectID;

  const {
    data: dashboardData,
    refetch: fetchData,
    isLoading: isDataLoading
  } = useQuery({
    queryKey: ['getMarketingDashBoardData', projectID, year],
    queryFn: () => getMarketingDashBoardData(projectID, year),
    enabled: !!parameters
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Marketing Dashboard Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    setisLoading(isDataLoading);
  }, [isDataLoading]);

  useEffect(() => {
    if (projectID) fetchData();
  }, [projectID]);

  const CustomTooltipForMonthBar = ({ payload, label, active }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{ backgroundColor: '#fff', padding: '5px', borderRadius: '5px' }}>
          <p>{label}</p>
          <p style={{ color: '#000000' }} className="font-semibold">
            Value: {data.lead_count}
          </p>
          {/* Value text is black */}
        </div>
      );
    }

    return null;
  };

  // --- BestMonthBar: cap values and set domain if needed ---
  const getCappedMonthData = () => {
    if (!dashboardData?.chartData) return [];
    return dashboardData.chartData.map((item) => ({
      ...item,
      capped_lead_count: item.lead_count > 40 ? 40 : item.lead_count,
      actual_lead_count: item.lead_count
    }));
  };
  const monthData = getCappedMonthData();
  const hasMonthOver40 = dashboardData?.chartData?.some((item) => item.lead_count > 40);

  const BestMonthBar = () => {
    const hasData = monthData?.some((item) => item.actual_lead_count > 0);

    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Best Performing Month</h2>
          <div className="flex items-center space-x-2 text-gray-500">
            <button onClick={decrementYear} className="hover:text-black">
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm">{year}</span>
            <button onClick={incrementYear} className="hover:text-black">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthData}>
              <XAxis dataKey="month" hide />
              <YAxis allowDecimals={false} domain={hasMonthOver40 ? [0, 40] : undefined} />
              <Tooltip content={<CustomTooltipForMonthBar />} />
              <Bar dataKey="capped_lead_count" fill="#c6e7e9" radius={[10, 10, 0, 0]}>
                <LabelList
                  dataKey="month"
                  position="insideBottom"
                  style={{ fill: '#000000', fontSize: 13, fontWeight: '500' }}
                />
                <LabelList
                  dataKey="actual_lead_count"
                  position="insideTop"
                  style={{ fill: '#000000', fontWeight: 'bold', fontSize: 14 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex justify-center items-center">
            <img src={noDataIcon} alt="No Data" className="w-[20rem] h-[15rem] object-cover" />
          </div>
        )}
      </div>
    );
  };

  const COLORS = ['#50959F', '#67ACB6', '#81C1CB', '#A3D6DE', '#C0EBF0'];
  const totalCount = dashboardData?.topLocations?.reduce(
    (acc, location) => acc + location.count,
    0
  );

  const TopLocationsPie = () => {
    const hasLocations = dashboardData?.topLocations?.length > 0;

    return (
      <div>
        <h2 className="text-lg font-semibold mb-2">Top 5 locations for leads</h2>
        {hasLocations ? (
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={dashboardData?.topLocations}
                cx="40%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="count">
                {dashboardData?.topLocations?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
                <LabelList
                  dataKey="count"
                  position="inside"
                  style={{
                    fill: 'black',
                    fontWeight: 'bold',
                    textShadow: 'none', // Ensuring no shadow
                    stroke: 'none' // Remove any outline around the text
                  }}
                  formatter={(value, entry, index) => `${Math.round((value / totalCount) * 100)}%`} // Display percentage inside pie
                />
              </Pie>
              <Tooltip formatter={(value) => `${Math.round((value / totalCount) * 100)}%`} />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                payload={dashboardData?.topLocations?.map((entry, index) => ({
                  value: entry.location,
                  type: 'square',
                  color: COLORS[index % COLORS.length]
                }))}
                content={({ payload }) => (
                  <ul>
                    {payload.map((entry, index) => (
                      <li
                        key={`legend-item-${index}`}
                        style={{ color: 'black', listStyle: 'none' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            backgroundColor: entry.color,
                            marginRight: 8
                          }}></span>
                        {entry.value}
                      </li>
                    ))}
                  </ul>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex justify-center items-center">
            <img src={noDataIcon} alt="No Data" className="w-[20rem] h-[15rem] object-cover" />
          </div>
        )}
      </div>
    );
  };

  const vslData = [
    { name: 'No. of visitors', value: dashboardData?.totalVisitors || 0 },
    { name: 'No. of leads', value: dashboardData?.totalLeads || 0 },
    { name: 'Booked', value: dashboardData?.booked || 0 }
  ];

  // --- VisitorsLeadsBooked: cap values and set domain if needed ---
  const getCappedVSLData = () => {
    return vslData.map((item) => ({
      ...item,
      capped_value: item.value > 40 ? 40 : item.value,
      actual_value: item.value
    }));
  };
  const cappedVSLData = getCappedVSLData();
  const hasVSLOutOf40 = vslData.some((item) => item.value > 40);

  const CustomTooltip = ({ payload, label, active }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{ backgroundColor: '#fff', padding: '5px', borderRadius: '5px' }}>
          <p>{label}</p>
          <p style={{ color: '#000000' }} className="font-semibold">
            Value: {data.value}
          </p>
          {/* Value text is black */}
        </div>
      );
    }

    return null;
  };

  const VisitorsLeadsBooked = () => {
    const hasData = cappedVSLData?.some((item) => item.actual_value > 0);

    return (
      <div>
        <h2 className="text-lg font-semibold mb-2">Visitors vs Leads vs Booked</h2>
        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart layout="vertical" data={cappedVSLData} margin={{ left: 5 }}>
              <XAxis
                type="number"
                allowDecimals={false}
                domain={hasVSLOutOf40 ? [0, 40] : undefined}
              />
              <YAxis type="category" dataKey="name" />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="capped_value" fill="#d8eeef" barSize={30} radius={[0, 10, 10, 0]}>
                {/* <LabelList
                  dataKey="name"
                  position="insideLeft"
                  style={{ fill: '#000000', fontSize: 13, fontWeight: '500' }}
                /> */}
                <LabelList
                  dataKey="actual_value"
                  position="insideRight"
                  formatter={(value) => (value === 0 ? '' : value)}
                  style={{ fill: '#000000', fontWeight: 'bold', fontSize: 15 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex justify-center items-center">
            <img src={noDataIcon} alt="No Data" className="w-[20rem] h-[15rem] object-cover" />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Loader isVisible={isloading || isProjectsLoading} />
      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            backArrow={showHomeBack}
            onBack={showHomeBack ? onBack : undefined}
            data={{ name: 'Dashboard' }}
          />
          <DashboardFilters
            onFiltersChange={handleFiltersChange}
            top="4rem"
            showMonth={false}
          />
        </div>
        {/* Grid - adjusted margin top to account for new tab position */}
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <GridWrapper width={100}>
            <div className="flex flex-col gap-4 mt-[11rem]">
              <AiPulsePanel projectId={projectID} defaultVertical="marketing" />

              {/* Project Wise Channel */}
              <div className="flex gap-4">
                <div className="flex w-[70%] flex-col gap-3 bg-white rounded-xl p-4">
                  <label htmlFor="label" className="font-semibold text-[1.4rem]">
                    Project wise best channel
                  </label>
                  <div className="flex gap-4">
                    <label
                      htmlFor={dashboardData?.bestChannel}
                      className="font-semibold text-[1.2rem]">
                      {dashboardData?.bestChannel}
                    </label>
                  </div>
                </div>

                <div className="flex w-[30%] flex-col gap-3 bg-white rounded-xl p-4">
                  <label htmlFor="label" className="font-semibold text-[1.4rem]">
                    Return of Investment
                  </label>
                  {/* <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do</p> */}
                  <p className="text-[#2A565E] font-bold text-[1.7rem]">
                    ₹ {dashboardData?.ROI || 0}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                {/* Top 5 Locations */}
                <div className="w-[40%] bg-white rounded-xl p-4">
                  <TopLocationsPie />
                </div>

                {/* Visitors vs Leads vs Booked */}
                <div className="w-[60%] bg-white rounded-xl p-4">
                  <VisitorsLeadsBooked />
                </div>
              </div>

              {/* Best Month (Bar) */}
              <div className="flex gap-4">
                <div className="w-[70%] bg-white p-4">
                  <BestMonthBar />
                </div>
                <div className="flex flex-col gap-3 p-4 rounded-xl bg-white w-[30%]">
                  <label htmlFor="" className="font-semibold text-[1.4rem]">
                    Best Performing Campaigns
                  </label>

                  {dashboardData?.top5CampaignsByLeads?.length > 0 ? (
                    dashboardData.top5CampaignsByLeads.map((item, index) => (
                      <p key={index} className="font-semibold">
                        {item?.campaign?.name} #{index + 1}
                      </p>
                    ))
                  ) : (
                    <div className="flex justify-center items-center">
                      <img
                        src={noDataIcon}
                        alt="No Data"
                        className="w-[20rem] h-[15rem] object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </GridWrapper>
        </div>
      </Layout>
    </>
  );
};

export default MarketingDashboard;
